import React, { Component } from "react";
import PropTypes from "prop-types";
import styled, { css } from "styled-components";
import platformLogo from "asset/image/trackcode-logo.png";
import {
  readDemoModeState,
  setDemoModeEnabled,
  setWencoDemoModeEnabled
} from "common/demoModeControls";
import { isTestEnvironment } from "common/config";
import { isTrackCodeInternalUser } from "common/trackcodeInternal";
import { resolveSidebarPlatformSettings } from "common/platformSettings";
import {
  getActiveLogisticCompanyName,
  resolveActiveLogisticCompanyId
} from "./logisticCompanySwitcherUtils";
import {
  SIDEBAR_NAV_ICON_COMPONENTS,
  SidebarIconPanelLeft
} from "./icons";
import TcCollapsedLogo from "./TcCollapsedLogo";
import {
  APP_BAR_HEIGHT,
  PLATFORM_MODULE,
  PLATFORM_VERSION,
  SIDEBAR_COLLAPSED_HOVER_MARKER_HEIGHT,
  SIDEBAR_COLLAPSED_HOVER_MARKER_WIDTH,
  SIDEBAR_COLLAPSED_INSET,
  SIDEBAR_COLLAPSED_LOGO_HEIGHT,
  SIDEBAR_COLLAPSED_LOGO_RADIUS,
  SIDEBAR_COLLAPSE_ICON_EXPANDED,
  SIDEBAR_LOGO_HEIGHT,
  SIDEBAR_COLOR_ACTIVE,
  SIDEBAR_COLOR_BG,
  SIDEBAR_COLOR_BORDER,
  SIDEBAR_AVATAR_BG,
  SIDEBAR_AVATAR_COLOR,
  SIDEBAR_AVATAR_FONT_SIZE,
  SIDEBAR_AVATAR_SIZE,
  SIDEBAR_COLOR_GROUP_LABEL,
  SIDEBAR_COLOR_HOVER,
  SIDEBAR_COLOR_ICON,
  SIDEBAR_COLOR_TEXT,
  SIDEBAR_COLOR_TEXT_MUTED,
  SIDEBAR_FOOTER_FONT_SIZE,
  SIDEBAR_FOOTER_GAP,
  SIDEBAR_FOOTER_PADDING_X,
  SIDEBAR_FOOTER_PADDING_Y,
  SIDEBAR_FOOTER_SUBTITLE_FONT_SIZE,
  SIDEBAR_NAV_ICON_GAP,
  SIDEBAR_NAV_ITEM_MARGIN_X,
  SIDEBAR_NAV_PADDING_X,
  SIDEBAR_NAV_PADDING_Y,
  SIDEBAR_PADDING_X,
  SIDEBAR_TOGGLE_SIZE,
  SIDEBAR_TRANSITION,
  SIDEBAR_WIDTH_COLLAPSED,
  SIDEBAR_WIDTH_EXPANDED,
  getPlatformSidebarWidth,
  writePlatformSidebarCollapsed
} from "./platformSidebarConstants";
import {
  getActiveModule,
  getVisibleNavGroups,
  isPlatformSidebarCollapsedForPath,
  platformSidebarNavGroups
} from "./platformSidebarNav";

class PlatformSidebar extends Component {
  static propTypes = {
    user: PropTypes.shape({
      name: PropTypes.string,
      firstname: PropTypes.string,
      lastname: PropTypes.string,
      logisticcompany_id: PropTypes.number
    }),
    logisticCompanyId: PropTypes.number,
    managedLogisticCompanies: PropTypes.object,
    platformSettings: PropTypes.oneOfType([
      PropTypes.array,
      PropTypes.object
    ]),
    homePlatformSettings: PropTypes.oneOfType([
      PropTypes.array,
      PropTypes.object
    ]),
    pathname: PropTypes.string.isRequired,
    onNavigate: PropTypes.func.isRequired,
    onSettingsClick: PropTypes.func.isRequired,
    onLogout: PropTypes.func,
    onWidthChange: PropTypes.func
  };

  static defaultProps = {
    user: null,
    logisticCompanyId: 0,
    managedLogisticCompanies: {},
    platformSettings: null,
    homePlatformSettings: null,
    onLogout: null,
    onWidthChange: null
  };

  constructor(props) {
    super(props);
    this.state = {
      collapsed: isPlatformSidebarCollapsedForPath(props.pathname),
      profileMenuOpen: false,
      ...readDemoModeState(props.managedLogisticCompanies)
    };
  }

  componentDidMount() {
    document.addEventListener("mousedown", this.handleProfileMenuPointerDown);
    document.addEventListener("touchstart", this.handleProfileMenuPointerDown);
    writePlatformSidebarCollapsed(this.state.collapsed);
    this.notifyWidthChange();
  }

  componentWillUnmount() {
    document.removeEventListener("mousedown", this.handleProfileMenuPointerDown);
    document.removeEventListener("touchstart", this.handleProfileMenuPointerDown);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.managedLogisticCompanies !== this.props.managedLogisticCompanies) {
      this.setState(readDemoModeState(this.props.managedLogisticCompanies));
    }
    if (prevProps.pathname !== this.props.pathname) {
      this.syncCollapsedToRoute();
    }
  }

  syncCollapsedToRoute = () => {
    const collapsed = isPlatformSidebarCollapsedForPath(this.props.pathname);
    if (collapsed === this.state.collapsed) {
      return;
    }
    this.setState({ collapsed }, () => {
      writePlatformSidebarCollapsed(this.state.collapsed);
      this.notifyWidthChange();
    });
  };

  setProfileMenuRef = ref => {
    this.profileMenuRef = ref;
  };

  handleProfileMenuPointerDown = event => {
    const { profileMenuOpen } = this.state;
    if (!profileMenuOpen) {
      return;
    }
    const isInside = this.profileMenuRef
      ? this.profileMenuRef.contains(event.target)
      : false;
    if (!isInside) {
      this.setState({ profileMenuOpen: false });
    }
  };

  toggleProfileMenu = event => {
    if (event) {
      event.stopPropagation();
    }
    this.setState(prevState => ({
      profileMenuOpen: !prevState.profileMenuOpen,
      ...(!prevState.profileMenuOpen
        ? readDemoModeState(this.props.managedLogisticCompanies)
        : {})
    }));
  };

  closeProfileMenu = () => {
    this.setState({ profileMenuOpen: false });
  };

  toggleDemoMode = () => {
    const nextEnabled = !this.state.demoModeEnabled;
    setDemoModeEnabled(nextEnabled);
    this.setState({ demoModeEnabled: nextEnabled });
  };

  toggleWencoDemoMode = () => {
    const nextEnabled = !this.state.wencoDemoModeEnabled;
    setWencoDemoModeEnabled(nextEnabled);
    this.setState({ wencoDemoModeEnabled: nextEnabled });
  };

  handleProfileMenuAction = action => () => {
    if (action === "settings") {
      this.closeProfileMenu();
      this.props.onSettingsClick();
      return;
    }
    if (action === "demo-mode") {
      this.toggleDemoMode();
      return;
    }
    if (action === "wenco-demo-mode") {
      this.toggleWencoDemoMode();
      return;
    }
    if (action === "logout") {
      this.closeProfileMenu();
      const { onLogout } = this.props;
      if (typeof onLogout === "function") {
        onLogout();
      }
    }
  };

  toggleCollapsed = () => {
    this.setState(
      prevState => ({
        collapsed: !prevState.collapsed
      }),
      () => {
        writePlatformSidebarCollapsed(this.state.collapsed);
        this.notifyWidthChange();
      }
    );
  };

  notifyWidthChange = () => {
    const { onWidthChange } = this.props;
    if (typeof onWidthChange === "function") {
      onWidthChange(getPlatformSidebarWidth(this.state.collapsed));
    }
  };

  handleNavItemClick = item => () => {
    if (item.inactive || item.hint || item.action === "none") {
      return;
    }
    if (item.action === "settings") {
      this.props.onSettingsClick();
      return;
    }
    if (item.action === "navigate") {
      const collapsed = item.key !== PLATFORM_MODULE.COLLABORATION;
      if (collapsed !== this.state.collapsed) {
        this.setState({ collapsed }, () => {
          writePlatformSidebarCollapsed(this.state.collapsed);
          this.notifyWidthChange();
        });
      }
      this.props.onNavigate(item.key);
    }
  };

  getProfileName = () => {
    const { user } = this.props;
    if (!user) {
      return "Benutzer";
    }
    if (user.name) {
      return user.name;
    }
    return [user.firstname, user.lastname].filter(Boolean).join(" ") || "Benutzer";
  };

  getProfileFirstName = () => {
    const { user } = this.props;
    if (!user) {
      return "Benutzer";
    }
    if (user.firstname?.trim()) {
      return user.firstname.trim();
    }
    const name = user.name?.trim();
    if (name) {
      return name.split(/\s+/)[0];
    }
    return this.getProfileName();
  };

  getProfileInitials = () => {
    const { user } = this.props;
    const first = user?.firstname?.trim();
    const last = user?.lastname?.trim();
    if (first && last) {
      return (first.charAt(0) + last.charAt(0)).toUpperCase();
    }
    const parts = this.getProfileName()
      .trim()
      .split(/\s+/)
      .filter(Boolean);
    if (parts.length >= 2) {
      return (
        parts[0].charAt(0) + parts[parts.length - 1].charAt(0)
      ).toUpperCase();
    }
    if (parts.length === 1) {
      return parts[0].charAt(0).toUpperCase();
    }
    return "?";
  };

  getCompanyName = () => {
    const { user, logisticCompanyId, managedLogisticCompanies } = this.props;
    return (
      getActiveLogisticCompanyName({
        logisticCompanyId,
        user,
        managedLogisticCompanies
      }) || "TrackCode"
    );
  };

  hasProfileMenuItems = () => {
    const { onLogout, user } = this.props;
    const { hasDemoModeAccess, hasWencoDemoAccess } = this.state;
    return (
      isTrackCodeInternalUser(user) ||
      hasDemoModeAccess ||
      hasWencoDemoAccess ||
      typeof onLogout === "function"
    );
  };

  renderProfileMenuItems = () => {
    const { onLogout, user } = this.props;
    const {
      hasDemoModeAccess,
      hasWencoDemoAccess,
      demoModeEnabled,
      wencoDemoModeEnabled
    } = this.state;
    const items = [];

    if (isTrackCodeInternalUser(user)) {
      items.push({ key: "settings", label: "Einstellungen" });
    }
    if (hasDemoModeAccess) {
      items.push({
        key: "demo-mode",
        label: `Demo-Modus ${demoModeEnabled ? "deaktivieren" : "aktivieren"}`
      });
    }
    if (hasWencoDemoAccess) {
      items.push({
        key: "wenco-demo-mode",
        label: `DLB-Demo ${wencoDemoModeEnabled ? "deaktivieren" : "aktivieren"}`
      });
    }
    if (typeof onLogout === "function") {
      items.push({ key: "logout", label: "Abmelden" });
    }

    return items.map((item, index) => (
      <SidebarProfileMenuItem
        key={item.key}
        type="button"
        $withDivider={item.key === "logout" && index > 0}
        onClick={this.handleProfileMenuAction(item.key)}
      >
        {item.label}
      </SidebarProfileMenuItem>
    ));
  };

  renderProfileFooter = collapsed => {
    const { onLogout } = this.props;
    const { profileMenuOpen } = this.state;
    const profileName = this.getProfileName();
    const profileFirstName = this.getProfileFirstName();
    const companyName = this.getCompanyName();
    const profileInitials = this.getProfileInitials();
    const showProfileMenu = this.hasProfileMenuItems();

    if (!showProfileMenu && typeof onLogout !== "function") {
      return null;
    }

    return (
      <SidebarFooterProfile
        ref={this.setProfileMenuRef}
        $collapsed={collapsed}
      >
        <SidebarProfileButton
          type="button"
          $collapsed={collapsed}
          aria-expanded={profileMenuOpen}
          aria-haspopup="menu"
          title={profileName}
          onClick={showProfileMenu ? this.toggleProfileMenu : undefined}
        >
          <SidebarProfileAvatar>{profileInitials}</SidebarProfileAvatar>
          {!collapsed && (
            <SidebarFooterProfileText>
              <SidebarFooterProfileName>
                {profileFirstName}
              </SidebarFooterProfileName>
              <SidebarFooterProfileSub>{companyName}</SidebarFooterProfileSub>
            </SidebarFooterProfileText>
          )}
        </SidebarProfileButton>
        {profileMenuOpen && (
          <SidebarProfileMenu $collapsed={collapsed} role="menu">
            {this.renderProfileMenuItems()}
          </SidebarProfileMenu>
        )}
      </SidebarFooterProfile>
    );
  };

  renderNavIcon = iconName => {
    const IconComponent = SIDEBAR_NAV_ICON_COMPONENTS[iconName];
    return IconComponent ? <IconComponent /> : null;
  };

  renderNavItem = (item, collapsed, activeModule) => {
    if (item.hint && collapsed) {
      return null;
    }
    const isInactive = Boolean(item.inactive);
    const isHint = Boolean(item.hint);
    const isActive =
      item.action === "navigate" && activeModule === item.key && !isInactive;
    const isInteractive = item.action === "navigate" || item.action === "settings";

    return (
      <SidebarNavButton
        key={item.key}
        type="button"
        $collapsed={collapsed}
        $isActive={isActive}
        $inactive={isInactive}
        $hint={isHint}
        title={item.title}
        disabled={isInactive || isHint || !isInteractive}
        onClick={this.handleNavItemClick(item)}
      >
        <SidebarNavIcon $inactive={isInactive || isHint}>
          {this.renderNavIcon(item.icon)}
        </SidebarNavIcon>
        <SidebarNavLabel $collapsed={collapsed} $hint={isHint}>
          {item.label}
        </SidebarNavLabel>
        {item.badge && !collapsed && (
          <SidebarNavBadge>{item.badge}</SidebarNavBadge>
        )}
      </SidebarNavButton>
    );
  };

  renderNavItems = (collapsed, activeModule) => {
    const { user, logisticCompanyId, homePlatformSettings, platformSettings } =
      this.props;
    const isInternalUser = isTrackCodeInternalUser(user);
    const activeCompanyId = resolveActiveLogisticCompanyId(
      logisticCompanyId,
      user
    );
    const settings = resolveSidebarPlatformSettings({
      user,
      logisticCompanyId,
      homePlatformSettings,
      platformSettings
    });
    const groups = getVisibleNavGroups(platformSidebarNavGroups, {
      isInternalUser,
      settings,
      logisticCompanyId: activeCompanyId,
      isTestEnvironment
    });

    return groups.map((group, groupIndex) => (
      <SidebarNavGroup
        key={group.label || `group-${groupIndex}`}
        $pinBottom={group.pin === "bottom"}
      >
        {collapsed && groupIndex > 0 && <SidebarGroupDivider />}
        {group.label && (
          <SidebarGroupLabel $collapsed={collapsed}>
            {group.label}
          </SidebarGroupLabel>
        )}
        {group.items.map(item =>
          this.renderNavItem(item, collapsed, activeModule)
        )}
      </SidebarNavGroup>
    ));
  };

  render() {
    const { collapsed } = this.state;
    const activeModule = getActiveModule(this.props.pathname);

    return (
      <SidebarContainer collapsed={collapsed}>
        <SidebarTop collapsed={collapsed}>
          {collapsed ? (
            <SidebarCollapsedHeader>
              <SidebarCollapsedBrand
                type="button"
                title="Seitenleiste öffnen"
                aria-label="Seitenleiste öffnen"
                onClick={this.toggleCollapsed}
              >
                <TcCollapsedLogo
                  className="sidebar-collapsed-logo"
                  size={SIDEBAR_COLLAPSED_LOGO_HEIGHT}
                />
                <SidebarIconPanelLeft className="sidebar-expand-icon" size={18} />
              </SidebarCollapsedBrand>
            </SidebarCollapsedHeader>
          ) : (
            <SidebarExpandedHeader>
              <SidebarLogoWrap>
                <SidebarLogo src={platformLogo} alt="TrackCode" />
                <SidebarVersionMark>{PLATFORM_VERSION}</SidebarVersionMark>
              </SidebarLogoWrap>
              <SidebarToggleButton
                type="button"
                title="Seitenleiste schließen"
                aria-label="Seitenleiste schließen"
                onClick={this.toggleCollapsed}
              >
                <SidebarIconPanelLeft size={SIDEBAR_COLLAPSE_ICON_EXPANDED} />
              </SidebarToggleButton>
            </SidebarExpandedHeader>
          )}
        </SidebarTop>
        <SidebarNav $collapsed={collapsed}>
          {this.renderNavItems(collapsed, activeModule)}
        </SidebarNav>
        <SidebarFooter>
          {collapsed
            ? this.renderProfileFooter(true)
            : this.renderProfileFooter(false)}
        </SidebarFooter>
      </SidebarContainer>
    );
  }
}

const SidebarContainer = styled.aside`
  width: ${({ collapsed }) =>
    collapsed ? `${SIDEBAR_WIDTH_COLLAPSED}px` : `${SIDEBAR_WIDTH_EXPANDED}px`};
  min-width: ${({ collapsed }) =>
    collapsed ? `${SIDEBAR_WIDTH_COLLAPSED}px` : `${SIDEBAR_WIDTH_EXPANDED}px`};
  height: 100%;
  border-right: 1px solid ${SIDEBAR_COLOR_BORDER};
  background: ${SIDEBAR_COLOR_BG};
  color: ${SIDEBAR_COLOR_TEXT};
  padding: ${({ collapsed }) =>
    collapsed ? "0 0 6px" : `0 ${SIDEBAR_PADDING_X}px 6px`};
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition: width ${SIDEBAR_TRANSITION}, min-width ${SIDEBAR_TRANSITION},
    padding ${SIDEBAR_TRANSITION};

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const SidebarTop = styled.div`
  margin-bottom: 12px;
  width: 100%;
  height: ${APP_BAR_HEIGHT}px;
  min-height: ${APP_BAR_HEIGHT}px;
  display: flex;
  align-items: center;
`;

const SidebarExpandedHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  width: 100%;
  height: ${APP_BAR_HEIGHT}px;
  min-height: ${APP_BAR_HEIGHT}px;
`;

const SidebarLogoWrap = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: baseline;
  gap: 6px;
  height: ${SIDEBAR_LOGO_HEIGHT}px;
  padding-left: ${SIDEBAR_NAV_PADDING_X}px;
`;

const SidebarVersionMark = styled.span`
  flex-shrink: 0;
  font-size: 11px;
  font-weight: 400;
  line-height: 1;
  color: #8c919f;
  letter-spacing: 0;
`;

const SidebarCollapsedHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: ${APP_BAR_HEIGHT}px;
  min-height: ${APP_BAR_HEIGHT}px;
  padding: 0 ${SIDEBAR_COLLAPSED_INSET}px;
  box-sizing: border-box;
`;

const SidebarCollapsedBrand = styled.button`
  position: relative;
  width: ${SIDEBAR_COLLAPSED_LOGO_HEIGHT}px;
  height: ${SIDEBAR_COLLAPSED_LOGO_HEIGHT}px;
  border: 0;
  border-radius: ${SIDEBAR_COLLAPSED_LOGO_RADIUS}px;
  background: #0a7c4e;
  padding: 0;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;

  .sidebar-collapsed-logo {
    display: block;
    color: #ffffff;
    flex-shrink: 0;
  }

  .sidebar-expand-icon {
    display: none;
    color: #ffffff;
  }

  &:hover {
    background: #0d9560;

    .sidebar-collapsed-logo {
      display: none;
    }

    .sidebar-expand-icon {
      display: inline-flex;
      color: #ffffff;
    }
  }
`;

const SidebarLogo = styled.img`
  height: ${SIDEBAR_LOGO_HEIGHT}px;
  width: auto;
  max-width: 100%;
  display: block;
  object-fit: contain;
  object-position: left center;
  flex-shrink: 0;
  transition: opacity 0.2s ease;
`;

const SidebarToggleButton = styled.button`
  width: ${SIDEBAR_TOGGLE_SIZE}px;
  height: ${SIDEBAR_TOGGLE_SIZE}px;
  flex-shrink: 0;
  border-radius: 6px;
  border: 0;
  background: transparent;
  color: #6a6960;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;

  &:hover {
    background: ${SIDEBAR_COLOR_ACTIVE};
    color: ${SIDEBAR_COLOR_TEXT};
  }
`;

const SidebarNav = styled.nav`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  gap: ${({ $collapsed }) => ($collapsed ? "0" : "8px")};
  width: 100%;
`;

const SidebarNavGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  ${({ $pinBottom }) =>
    $pinBottom &&
    css`
      margin-top: auto;
      padding-top: 8px;
    `}
`;

const SidebarGroupDivider = styled.div`
  height: 1px;
  background: ${SIDEBAR_COLOR_BORDER};
  margin: 8px ${SIDEBAR_COLLAPSED_INSET}px;
  flex-shrink: 0;
`;

const SidebarGroupLabel = styled.div`
  padding: 14px 16px 4px;
  font-size: 10px;
  color: ${SIDEBAR_COLOR_GROUP_LABEL};
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-weight: 600;
  white-space: nowrap;
  display: ${({ $collapsed }) => ($collapsed ? "none" : "block")};
`;

const collapsedNavMarkerRight =
  SIDEBAR_COLLAPSED_INSET + SIDEBAR_COLLAPSED_HOVER_MARKER_WIDTH / 2;

const collapsedNavMarkerStyles = css`
  content: "";
  position: absolute;
  right: -${collapsedNavMarkerRight}px;
  top: 50%;
  transform: translateY(-50%);
  width: ${SIDEBAR_COLLAPSED_HOVER_MARKER_WIDTH}px;
  height: ${SIDEBAR_COLLAPSED_HOVER_MARKER_HEIGHT}px;
  background: ${SIDEBAR_COLOR_TEXT};
  border-radius: 999px;
  pointer-events: none;
  z-index: 2;
`;

const SidebarNavButton = styled.button`
  text-align: left;
  border: 0;
  border-radius: 6px;
  background: ${({ $isActive }) =>
    $isActive ? SIDEBAR_COLOR_ACTIVE : "transparent"};
  color: ${({ $collapsed, $isActive, $inactive, $hint }) => {
    if ($inactive || $hint) {
      return SIDEBAR_COLOR_GROUP_LABEL;
    }
    if ($collapsed) {
      return $isActive ? SIDEBAR_COLOR_TEXT : SIDEBAR_COLOR_ICON;
    }
    return $isActive ? SIDEBAR_COLOR_TEXT : SIDEBAR_COLOR_TEXT_MUTED;
  }};
  font-size: ${({ $hint }) => ($hint ? "13px" : "13px")};
  font-weight: 500;
  font-style: ${({ $hint }) => ($hint ? "italic" : "normal")};
  width: ${({ $collapsed }) => ($collapsed ? "auto" : "auto")};
  min-height: ${({ $collapsed }) => ($collapsed ? "auto" : "30px")};
  margin: ${({ $collapsed }) =>
    $collapsed
      ? `0 ${SIDEBAR_COLLAPSED_INSET}px`
      : `0 ${SIDEBAR_NAV_ITEM_MARGIN_X}px`};
  padding: ${({ $collapsed }) =>
    $collapsed
      ? "8px 0"
      : `${SIDEBAR_NAV_PADDING_Y}px ${SIDEBAR_NAV_PADDING_X}px`};
  box-sizing: border-box;
  cursor: ${({ $inactive, $hint }) =>
    $inactive || $hint ? "default" : "pointer"};
  display: flex;
  align-items: center;
  justify-content: ${({ $collapsed }) => ($collapsed ? "center" : "flex-start")};
  flex-shrink: 0;
  align-self: stretch;
  position: relative;
  overflow: ${({ $collapsed }) => ($collapsed ? "visible" : "hidden")};
  transition: background 0.2s ease, color 0.2s ease;

  &:hover {
    background: ${({ $isActive, $inactive, $hint }) => {
      if ($inactive || $hint) {
        return "transparent";
      }
      return $isActive ? SIDEBAR_COLOR_ACTIVE : SIDEBAR_COLOR_HOVER;
    }};
    color: ${({ $inactive, $hint }) =>
      $inactive || $hint ? SIDEBAR_COLOR_GROUP_LABEL : SIDEBAR_COLOR_TEXT};
  }

  &:disabled {
    opacity: 1;
  }

  ${({ $collapsed, $inactive, $hint }) =>
    $collapsed &&
    !$inactive &&
    !$hint &&
    css`
      &:hover::after {
        ${collapsedNavMarkerStyles}
      }
    `}
`;

const SidebarNavIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  color: inherit;
  opacity: ${({ $inactive }) => ($inactive ? 0.85 : 1)};

  svg {
    display: block;
    width: 18px;
    height: 18px;
  }
`;

const SidebarNavLabel = styled.span`
  flex: ${({ $collapsed }) => ($collapsed ? "0 0 0" : "1")};
  min-width: 0;
  line-height: 20px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  opacity: ${({ $collapsed }) => ($collapsed ? 0 : 1)};
  max-width: ${({ $collapsed }) => ($collapsed ? 0 : "180px")};
  width: ${({ $collapsed }) => ($collapsed ? 0 : "auto")};
  margin-left: ${({ $collapsed }) =>
    $collapsed ? 0 : `${SIDEBAR_NAV_ICON_GAP}px`};
  font-style: ${({ $hint }) => ($hint ? "italic" : "inherit")};
  transition: opacity ${SIDEBAR_TRANSITION}, max-width ${SIDEBAR_TRANSITION},
    margin-left ${SIDEBAR_TRANSITION};

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const SidebarNavBadge = styled.span`
  margin-left: auto;
  padding-left: 8px;
  flex-shrink: 0;
  font-size: 9.5px;
  font-weight: 600;
  letter-spacing: 0.085em;
  text-transform: uppercase;
  color: ${SIDEBAR_COLOR_GROUP_LABEL};
  white-space: nowrap;
`;

const SidebarFooter = styled.div`
  margin-top: 0;
  flex-shrink: 0;
`;

const SidebarProfileAvatar = styled.div`
  width: ${SIDEBAR_AVATAR_SIZE}px;
  height: ${SIDEBAR_AVATAR_SIZE}px;
  border-radius: 50%;
  background: ${SIDEBAR_AVATAR_BG};
  color: ${SIDEBAR_AVATAR_COLOR};
  font-size: ${SIDEBAR_AVATAR_FONT_SIZE}px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  letter-spacing: -0.02em;
`;

const SidebarFooterProfile = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  padding: ${({ $collapsed }) =>
    $collapsed
      ? `${SIDEBAR_FOOTER_PADDING_Y}px 0`
      : `${SIDEBAR_FOOTER_PADDING_Y}px ${SIDEBAR_FOOTER_PADDING_X}px`};
  border-top: 1px solid ${SIDEBAR_COLOR_BORDER};
  font-size: ${SIDEBAR_FOOTER_FONT_SIZE}px;
  box-sizing: border-box;
  justify-content: ${({ $collapsed }) => ($collapsed ? "center" : "flex-start")};
`;

const SidebarProfileButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${SIDEBAR_FOOTER_GAP}px;
  width: ${({ $collapsed }) => ($collapsed ? "auto" : "100%")};
  min-width: 0;
  border: 0;
  background: transparent;
  padding: 0;
  margin: 0;
  cursor: pointer;
  text-align: left;
  color: inherit;
  border-radius: 6px;

  &:hover {
    background: ${SIDEBAR_COLOR_HOVER};
  }
`;

const SidebarProfileMenu = styled.div`
  position: absolute;
  left: ${({ $collapsed }) => ($collapsed ? "100%" : "8px")};
  right: ${({ $collapsed }) => ($collapsed ? "auto" : "8px")};
  bottom: calc(100% + 6px);
  min-width: ${({ $collapsed }) => ($collapsed ? "220px" : "auto")};
  margin-left: ${({ $collapsed }) => ($collapsed ? "8px" : "0")};
  padding: 6px;
  border: 1px solid ${SIDEBAR_COLOR_BORDER};
  border-radius: 8px;
  background: #ffffff;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);
  z-index: 30;
`;

const SidebarProfileMenuItem = styled.button`
  display: block;
  width: 100%;
  border: 0;
  background: transparent;
  padding: 8px 10px;
  margin: 0;
  text-align: left;
  font-size: 13px;
  line-height: 1.3;
  color: ${SIDEBAR_COLOR_TEXT};
  cursor: pointer;
  border-radius: 6px;
  border-top: ${({ $withDivider }) =>
    $withDivider ? `1px solid ${SIDEBAR_COLOR_BORDER}` : "0"};
  margin-top: ${({ $withDivider }) => ($withDivider ? "4px" : "0")};
  padding-top: ${({ $withDivider }) => ($withDivider ? "12px" : "8px")};

  &:hover {
    background: ${SIDEBAR_COLOR_HOVER};
  }
`;

const SidebarFooterProfileText = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  white-space: nowrap;
`;

const SidebarFooterProfileName = styled.span`
  font-weight: 500;
  line-height: 1.2;
  color: ${SIDEBAR_COLOR_TEXT};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SidebarFooterProfileSub = styled.span`
  font-size: ${SIDEBAR_FOOTER_SUBTITLE_FONT_SIZE}px;
  line-height: 1.2;
  color: ${SIDEBAR_COLOR_GROUP_LABEL};
  margin-top: 1px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export { getModulePath } from "./platformSidebarNav";
export {
  APP_BAR_HEIGHT,
  PLATFORM_MODULE,
  getPlatformSidebarWidth,
  readPlatformSidebarCollapsed
} from "./platformSidebarConstants";

export default PlatformSidebar;
